import { useContext } from 'react';
import { Breadcrumb, Button, Space, Tabs } from 'antd';
import { Link } from 'react-router-dom';
import { FaFilePdf, FaTrophy } from 'react-icons/fa';

import Loading from '../../../components/Loading';
import PageHeader from '../../../components/PageHeader';
import GiveawayStatusTag from '../components/GiveawayStatusTag';
import { ThemeContext } from '../../../contexts/ThemeContext';
import { convertDataUSParaBrasilEHora } from '../../../utils/Conversores';

import useViewGiveaway from './useViewGiveaway';
import TabInvoices from './components/Tabs/Invoices/TabInvoices';
import GiveawayParticipants from './components/Tabs/Participants';
import Tickets from './components/Tabs/Tickets';
import ReportStatsGiveaway from './components/ReportStatsGiveaway';
import GiveawayAwards from './components/Tabs/Awards';

export default function ViewGiveaway() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoading,
        statsGiveaway,
        statsPreviousGiveaway,
        giveawayData,
        previousGiveawayData,
        handlePrint,
        componentToPrintRef,
        ticketDistribution,
        canViewWinners,
        buttonReportDisabled,
        onChartAnimationEnd,
        activeTab
    } = useViewGiveaway();

    const tabs = [
        {
            key: '1',
            label: 'Estatísticas',
            children: (
                <ReportStatsGiveaway
                    refToPrint={componentToPrintRef}
                    onChartAnimationEnd={onChartAnimationEnd}
                    giveawayData={giveawayData}
                    previousGiveawayData={previousGiveawayData}
                    statsGiveaway={statsGiveaway}
                    statsPreviousGiveaway={statsPreviousGiveaway}
                    ticketDistribution={ticketDistribution}
                    canViewWinners={canViewWinners}
                />
            )
        },
        {
            key: '2',
            label: 'Bilhetes',
            children: <Tickets />
        },
        {
            key: '3',
            label: 'Notas',
            children: (
                <TabInvoices
                    startPeriod={giveawayData?.period_beginning}
                    endPeriod={giveawayData?.period_ending}
                />
            )
        },
    ];

    if (giveawayData) {
        tabs.push({
            key: '4',
            label: 'Participantes',
            children: <GiveawayParticipants giveawayId={giveawayData.id} />
        });
    }

    tabs.push({
        key: '5',
        label: 'Prêmios',
        children: <GiveawayAwards />
    });

    const getSubtitle = () => {
        if (!giveawayData) return "Visualização detalhada do sorteio e suas estatísticas";
        const parts: string[] = [];
        if (giveawayData.period_beginning && giveawayData.period_ending) {
            parts.push(`Período: ${convertDataUSParaBrasilEHora(giveawayData.period_beginning)} até ${convertDataUSParaBrasilEHora(giveawayData.period_ending)}`);
        }
        if (giveawayData.date) {
            parts.push(`Data do sorteio: ${convertDataUSParaBrasilEHora(giveawayData.date)}`);
        }
        return parts.join(' • ');
    };

    return (
        <Loading loading={isLoading}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* External Breadcrumb Card */}
                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 10,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: <Link to='/sorteios/geral'>Sorteios</Link>,
                            },
                            {
                                title: giveawayData?.description || `Visualizar sorteio`,
                            }
                        ]}
                    />
                </div>

                <PageHeader
                    icon={<FaTrophy />}
                    title={giveawayData?.description || "Visualizar Sorteio"}
                    subtitle={getSubtitle()}
                    tag={
                        giveawayData ? (
                            <GiveawayStatusTag
                                status={giveawayData.status}
                                isValid={giveawayData.is_valid}
                            />
                        ) : undefined
                    }
                    actions={
                        giveawayData && (
                            <Space align="center">
                                <Button
                                    onClick={() => handlePrint()}
                                    disabled={buttonReportDisabled}
                                    type='primary'
                                    icon={<FaFilePdf />}
                                    style={{
                                        borderRadius: 8,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontWeight: 500,
                                    }}
                                >
                                    Imprimir relatório
                                </Button>
                            </Space>
                        )
                    }
                />

                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 14,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '18px 20px',
                    }}
                >
                    <Tabs
                        defaultActiveKey={activeTab}
                        items={tabs}
                    />
                </div>
            </div>
        </Loading>
    );
}
